import {
  TextureLoader,
  RepeatWrapping,
  TangentSpaceNormalMap,
  ColorRepresentation,
  MeshStandardMaterial,
  BoxGeometry,
  Mesh,
  Object3D,
} from 'three'
import { useLoader } from '@react-three/fiber'
import { useVerandaSelector } from '../../../../../..'
import { CachedMeshPhysicalMaterial } from '../../../../../material/cachedMeshPhysicalMaterial'
import { useEffect, useState } from 'react'
import {
  AR_REPLACEMENT_SOURCE_MATERIAL_NAME,
  AR_REPLACEMENT_TARGET_MATERIAL_NAME,
} from '../../../../../../hooks/extension.hook'
import { useRoof } from '../../../../../../hooks/state/useRoof'
import { nanoid } from '@reduxjs/toolkit'

interface Props {
  opacity?: number | null
  roughness?: number | null
  isGlass?: boolean | null
  isOpal?: boolean | null
  color?: ColorRepresentation | '#FFF'
  parent?: Object3D
  hasPlank?: boolean
}

export const GlassOrPolyMaterial = ({
  roughness = null,
  opacity = null,
  isGlass = null,
  isOpal = null,
  color: glasColor = 'white',
  parent,
  hasPlank = false,
}: Props): JSX.Element => {
  const nightMode = useVerandaSelector((state) => state.scene.nightMode)
  const { color } = useRoof()

  const texture = useLoader(TextureLoader, `texture/glassStruct.jpg`)
  const normalMap = useLoader(TextureLoader, `texture/glassStruct_nmap.jpg`)
  const normalMapPlank = useLoader(
    TextureLoader,
    'texture/plank/planke_normal.jpg'
  )
  const roughnessMap = useLoader(
    TextureLoader,
    'texture/plank/planke_rough.jpg'
  )

  useEffect(() => {
    texture.repeat.set(24, 24)
    texture.wrapS = RepeatWrapping
    texture.wrapT = RepeatWrapping
  }, [texture])

  useEffect(() => {
    normalMap.repeat.set(24, 24)
    normalMap.wrapS = RepeatWrapping
    normalMap.wrapT = RepeatWrapping
  }, [normalMap])

  useEffect(() => {
    normalMapPlank.repeat.set(0, 1)
    normalMapPlank.wrapS = RepeatWrapping
    normalMapPlank.wrapT = RepeatWrapping
  }, [normalMapPlank])

  const envMapIntensity = nightMode ? 0.03 : 1

  let internalRoughness = roughness ?? 0.3
  let internalOpacity = opacity ?? 0.9

  if (
    isGlass !== null &&
    isOpal !== null &&
    opacity == null &&
    roughness == null
  ) {
    if (isGlass) {
      internalOpacity = 0.3
      internalRoughness = 0.3
      if (isOpal) {
        internalOpacity = 0.9
        internalRoughness = 0.5
      }
    } else {
      internalRoughness = 0.3
      if (isOpal) {
        internalOpacity = 0.5
      } else {
        internalOpacity = 0.95
      }
    }
  }

  /* This ensures that we can replace the Poly material with transmission
    by a material with opacity for the AR export later on. Directly creating
      the material on Export was not possible because since we use textures and
    etc. which requires the material to be rendered first before it is ready.
      */
  const [id, setId] = useState<string | null>(null)
  useEffect(() => {
    if (!parent) return

    const id = nanoid()
    setId(id)

    const sourceMaterial = new MeshStandardMaterial({
      name: AR_REPLACEMENT_SOURCE_MATERIAL_NAME + id,
      color: glasColor,
      roughness: 0.8,
      transparent: true,
      opacity: isOpal ? 0.98 : 0.7,
      map: texture,
      normalMap: normalMap,
      normalMapType: TangentSpaceNormalMap,
      envMapIntensity: 1,
      userData: { id },
    })

    const materialDummyName = AR_REPLACEMENT_SOURCE_MATERIAL_NAME + id
    const materialDummyBox = new BoxGeometry(1, 1, 1)
    const materialDummy = new Mesh(materialDummyBox, sourceMaterial)
    materialDummy.name = materialDummyName
    materialDummy.visible = false

    const existingDummy = parent.getObjectByName(
      AR_REPLACEMENT_SOURCE_MATERIAL_NAME + id
    )
    if (existingDummy) {
      parent.remove(existingDummy)
    }

    parent.add(materialDummy)
  }, [parent, glasColor])

  return (
    <>
      {isGlass && !hasPlank ? (
        <CachedMeshPhysicalMaterial
          color={glasColor}
          transparent
          opacity={internalOpacity}
          envMapIntensity={envMapIntensity}
          roughness={internalRoughness}
          metalness={1}
        />
      ) : hasPlank ? (
        <CachedMeshPhysicalMaterial
          name="PlankMaterial"
          color={color}
          // map={normalMapPlank}
          transmission={1}
          envMapIntensity={envMapIntensity}
          metalness={0.2}
          roughness={0.6}
          roughnessMap={roughnessMap}
          normalMap={normalMapPlank}
          normalMapType={TangentSpaceNormalMap}
          userData={{ id }}
        />
      ) : (
        <CachedMeshPhysicalMaterial
          name={AR_REPLACEMENT_TARGET_MATERIAL_NAME + id}
          color={glasColor}
          map={texture}
          transparent
          transmission={internalOpacity}
          envMapIntensity={envMapIntensity}
          metalness={0.25}
          roughness={internalRoughness}
          normalMap={normalMap}
          normalMapType={TangentSpaceNormalMap}
          userData={{ id }}
        />
      )}
    </>
  )
}
